 
import { LoadingIcon, OpenIcon } from '@blocksuite/affine-components/icons';
import type {
  EmbedVideoModel,
  EmbedVideoStyles,
} from '@blocksuite/affine-model';
import { ImageProxyService } from '@blocksuite/affine-shared/adapters';
import { ThemeProvider } from '@blocksuite/affine-shared/services';
import { BlockSelection } from '@blocksuite/std';
import { html, nothing } from 'lit';
import { property, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { styleMap } from 'lit/directives/style-map.js';
// import videojs from 'video.js';

import { EmbedBlockComponent } from '../common/embed-block-element.js';
import { getEmbedCardIcons } from '../common/utils.js';
import { videoUrlRegex } from './embed-video-model.js';
import type { EmbedVideoBlockService } from './embed-video-service.js';
import { styles, VideoIcon } from './styles.js';
import { refreshEmbedVideoUrlData } from './utils.js';
import { createRef, ref } from 'lit/directives/ref.js';
// @ts-expect-error – no typings shipped for bundled video.js
import VideoJs from './video.js';

type VideoJsInstance = {
  player: {
    src(arg0: { src: string }): unknown;
    dispose(): unknown;
  };
  el: HTMLElement;
  destroy: () => void;
  update: (opts: Record<string, unknown>) => void;
};

// @ts-expect-error
export class EmbedVideoBlockComponent extends EmbedBlockComponent<
  // @ts-ignore
  EmbedVideoModel,
  EmbedVideoBlockService
> {
  static override styles = styles;

  override _cardStyle: (typeof EmbedVideoStyles)[number] = 'video';

  open = () => {
    let link = this.model.props.url;
    if (!link.match(/^[a-zA-Z]+:\/\//)) {
      link = 'https://' + link;
    }
    window.open(link, '_blank');
  };

  refreshData = () => {
    refreshEmbedVideoUrlData(this, this.fetchAbortController.signal).catch(
      console.error
    );
  };

  private _handleDoubleClick(event: MouseEvent) {
    event.stopPropagation();
    this.open();
  }

  private _selectBlock() {
    const selectionManager = this.host.selection;
    const blockSelection = selectionManager.create(BlockSelection, {
      blockId: this.blockId,
    });
    selectionManager.setGroup('note', [blockSelection]);
  }

  protected _handleClick(event: MouseEvent) {
    event.stopPropagation();
    this._selectBlock();
  }

  // ✅ 保存 MediaQueryList 引用和事件处理函数，以便在 disconnectedCallback 中正确移除
  private _printMediaQuery: MediaQueryList | null = null;
  private readonly _printMediaHandler = () => {
    this._showImage = this._printMediaQuery?.matches ?? false;
  };

  override connectedCallback() {
    super.connectedCallback();
    this._cardStyle = this.model.props.style;

    if (!this.model.props.title) {
      this.store.withoutTransact(() => {
        const url = this.model.props.url;
        // Extract YouTube videoId only when the URL is a YouTube link
        const ytMatch = url.match(/\/([^\/?#]+)(?:\?[^#]*)?(?:#.*)?$/);
        const file = ytMatch ? decodeURIComponent(ytMatch[1]) : '';
        const title = file.replace(/\.[^./\\?&#]+$/, '') || file;
        if (title) {
          this.store.updateBlock(this.model, { title });
        }
      });
    }

    if (!this.model.props.description && !this.model.props.title) {
      this.store.withoutTransact(() => {
        this.refreshData();
      });
    }

    this.disposables.add(
      this.model.propsUpdated.subscribe(({ key }) => {
        this.requestUpdate();
        if (key === 'url') {
          this.refreshData();
        }
      })
    );

    // ✅ 保存 MediaQueryList 引用，确保添加和移除使用同一个对象
    this._printMediaQuery = matchMedia('print');
    this._printMediaQuery.addEventListener('change', this._printMediaHandler);
  }

  override renderBlock() {
    const { image, description, title } = this.model.props;

    const loading = this.loading;
    const theme = this.std.get(ThemeProvider).theme;
    const imageProxyService = this.store.get(ImageProxyService);
    const { EmbedCardBannerIcon } = getEmbedCardIcons(theme);

    const descriptionText = loading ? null : description || title;
    const bannerImage =
      !loading && image
        ? html`<img src=${imageProxyService.buildUrl(image)} alt="banner" />`
        : EmbedCardBannerIcon;

    // Determine domain for display
    const linkUrl = this.model.props.url;
    let domain = '';
    try {
      const normalized = linkUrl.match(/^[a-zA-Z]+:\/\//)
        ? linkUrl
        : 'https://' + linkUrl;
      domain = new URL(normalized).host || domain;
    } catch {}

    const isDirectVideo = !!linkUrl && videoUrlRegex.test(linkUrl);

    return this.renderEmbed(
      () => html`
        <div
          class=${classMap({
            'affine-embed-videojs-block': true,
            loading,
            selected: this.selected$.value,
          })}
          style=${styleMap({
            transformOrigin: '0 0',
          })}
          @click=${this._handleClick}
          @dblclick=${this._handleDoubleClick}
        >
          <div class="affine-embed-videojs-video">
            ${isDirectVideo
              ? html`<video
                  ${ref(this._videoRef)}
                  class="affine-embed-videojs video-js vjs-theme-city"
                  controls
                  preload="metadata"
                  src=${linkUrl}
                  style="width: 100%; height: 100%; border-radius: 4px;"
                ></video>`
              : bannerImage}
          </div>
          <div class="affine-embed-videojs-content">
            <div class="affine-embed-videojs-content-url" @click=${this.open}>
              ${VideoIcon}
              ${loading
                ? nothing
                : html`<a href=${linkUrl} target="_blank"
                    >${descriptionText}</a
                  >`}
            </div>
          </div>
        </div>
      `
    );
  }

  private readonly _videoRef = createRef<HTMLVideoElement>();

  override updated(): void {
    const linkUrl = this.model.props.url;
    const isDirectVideo = !!linkUrl && videoUrlRegex.test(linkUrl);
    const el = this._videoRef.value;
    // ✅ 合并嵌套的 if 语句
    if (isDirectVideo && el && !this._videoInstance) {
      const instance = VideoJs(el, {
        controls: true,
        preload: 'metadata',
        maxHeight: 400,
        maxWidth: '100%',
      }) as VideoJsInstance;
      instance.player.src({ src: linkUrl });

      this._videoInstance = instance;
    }
  }

  @state()
  private accessor _showImage = false;

  @property({ attribute: false })
  accessor loading = false;

  private accessor _videoInstance: VideoJsInstance | null = null;

  override disconnectedCallback(): void {
    // ✅ 使用保存的 MediaQueryList 引用移除事件监听器
    if (this._printMediaQuery) {
      this._printMediaQuery.removeEventListener('change', this._printMediaHandler);
      this._printMediaQuery = null;
    }

    // ✅ 调用 destroy() 清理 video.js 实例（包括 ResizeObserver）
    if (this._videoInstance) {
      this._videoInstance.destroy();
      this._videoInstance = null;
    }

    // ✅ 清理 video 元素引用
    if (this._videoRef.value) {
      // 移除 video 元素的所有事件监听器
      const video = this._videoRef.value;
      video.pause();
      video.src = '';
      video.load();
    }

    super.disconnectedCallback();
  }
}
